import React, { Component } from 'react';
import propTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { titleize, humanize } from 'underscore.string';
import CopyButton from 'components/v2/buttons/CopyButton';

import { withTranslation } from 'react-i18next';
import { snakify } from '../../../utils/localization/utils';

const ROLES = {
  admin: {
    description: 'Godmode Engaged.',
  },
  internal_account_administrator: {
    description: 'Internal Account Administrator',
  },
  integration_manager: {
    description: 'Integrations Manager',
  },
  accessorial_manager: {
    description: 'Accessorial Manager',
  },
  finance: {
    description: 'Accounting adjustments after invoice',
  },
  'finance-admin': {
    description: 'Manual stripe transfers',
  },
  locations_manager: {
    description: 'Manager of locations',
  },
  locations_admin: {
    description: 'Administrator of locations',
  },
  tariff: {
    description: 'Can Update Customer Tariffs',
  },
  'accounting-admin': {
    description: 'Can set accounting holds',
  },
  'message-admin': {
    description: 'Administrator of Messaging',
  },
  'message-manager': {
    description: 'Manager of Messaging',
  },
  'shipper-lane-pricing-manager': {
    description: 'Manager of Shipper Pricing for Rates',
  },
  'carrier-lane-pricing-manager': {
    description: 'Manager of Carrier Pricing for Rates',
  },
  'message-user': {
    description: 'Limited User of Messaging',
  },
  'message-group-user': {
    description: 'Can create group messages',
  },
  'filter-creator': {
    description: 'Create and share filters',
  },
  'trip-planner-admin': {
    description: 'Create create and apply trips',
  },
  'order-template-admin': {
    description: 'Create order templates',
  },
  support: {
    description: 'Can perform support related features',
  },
  'sales-rep': {
    description: 'Can be a sales rep on an account',
  },
  'carrier-compliance': {
    description: 'Can authorize or deauthorize carriers',
  },
  'aci-zones-admin': {
    description: 'Can update ACI zones',
  },
  'multi-stop-booking': {
    description: 'Can book shipments with a variable number of stops',
  },
  'metrics-admin': {
    description: 'Can export data across metric scorecards',
  },
  'account-admin': {
    description: 'Can administer account mapping/routing',
  },
  'bulk-pricing-manager': {
    description: 'Manage pricing tools',
  },
  'carrier-bulk-auditor': {
    description: 'Manage bulk audit tools',
  },
  customer_manager: {
    description:
      'Manage creation and updates of shipper, carrier and driver users',
  },
  'shipper-onboarding': {
    description:
      'Can edit any shipper account billing addresses above normal admin users',
  },
  'uv-report-manager': {
    description: 'Can manage all UV reports',
  },
  'reference-numbers-admin': {
    description: 'Can update system generated reference numbers',
  },
  'accessorials-admin': {
    description: 'Can force approvals of accessorial requests regardless of missing documents'
  },
  'billing-manager-admin': {
    description: 'Can manage invoice generation settings',
  },
};

class RolesPermissionsModal extends Component {
  static propTypes = {
    users: propTypes.array.isRequired,
    show: propTypes.bool.isRequired,
    onHide: propTypes.func.isRequired,
    onSave: propTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    const users = props.users.map((u) => {
      const first_name = (u.first_name || '').trim();
      const last_name = (u.last_name || '').trim();
      const name = `${first_name} ${last_name}`.trim();
      return {
        ...u,
        first_name,
        last_name,
        name,
      };
    });

    this.state = {
      users,
    };
  }

  mapRole = (role) => {
    const { t } = this.props;

    return humanize(titleize(t(snakify(role))));
  };

  hasRole(user, role) {
    return user.roles.indexOf(role) > -1;
  }

  handleUserRoleChange(user, role, checked) {
    let roles;
    const currentRoles = [...user.roles];
    if (checked) {
      roles = [...currentRoles, role];
    } else {
      const roleIndex = currentRoles.findIndex((r) => r === role);
      roles = [
        ...currentRoles.slice(0, roleIndex),
        ...currentRoles.slice(roleIndex + 1),
      ];
    }

    this.setState((prevState) => {
      const { users: currentUsers } = prevState;
      const index = currentUsers.findIndex((u) => u._id === user._id);
      const users = [
        ...currentUsers.slice(0, index),
        { ...user, roles },
        ...currentUsers.slice(index + 1),
      ];
      return {
        users,
      };
    });
  }

  handleApplyToFollowingUsers(user) {
    this.setState((prevState) => {
      const { users: currentUsers } = prevState;
      const roles = [...user.roles];

      const index = currentUsers.findIndex((u) => u._id === user._id);
      const followingUsers = [...currentUsers.slice(index + 1)];
      const users = [
        ...currentUsers.slice(0, index + 1),
        ...followingUsers.map((u) => {
          return { ...u, roles };
        }),
      ];
      return {
        users,
      };
    });
  }

  render() {
    const { t } = this.props;
    return (
      <Modal
        show={this.props.show}
        onHide={this.props.onHide}
        bsSize="large"
        center
      >
        <Modal.Header>
          <h3>{t('ROLES_AND_PERMISSIONS')}</h3>
        </Modal.Header>
        <Modal.Body>
          <div className="overflow-x-auto">
            <div>
              <p>{t('ROLES_AND_PERMISSIONS_TIP')}</p>
              <div className="flex py-4 mb-2 text-sm border-b border-gray-200 border-solid">
                <div className="w-1/4">&nbsp;</div>
                {Object.keys(ROLES).map((role) => {
                  return (
                    <div className="w-10 vertical-header form-label">
                      {this.mapRole(role)}
                    </div>
                  );
                })}
                <div className="w-20">&nbsp;</div>
              </div>
              {this.state.users.map((user, index) => {
                return (
                  <div className="flex mb-2 items-center" key={user._id}>
                    <div className="w-1/4">
                      <span className="text-sm font-medium">{user.name}</span>
                    </div>
                    {Object.keys(ROLES).map((role) => {
                      return (
                        <div className="w-10" key={role}>
                          <input
                            type="checkbox"
                            checked={this.hasRole(user, role)}
                            onChange={(e) => {
                              this.handleUserRoleChange(
                                user,
                                role,
                                e.target.checked,
                              );
                            }}
                          />
                        </div>
                      );
                    })}
                    <div className="w-20">
                      {this.state.users.length > index + 1 && (
                        <CopyButton
                          onClick={() => this.handleApplyToFollowingUsers(user)}
                          title={t('APPLY_TO_FOLLOWING_USERS')}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-center justify-end">
            <button
              type="button"
              className="mr-2 button button-green"
              onClick={() => this.props.onSave(this.state.users)}
            >
              {titleize(t('SAVE'))}
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={() => this.props.onHide()}
            >
              {titleize(t('CANCEL'))}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withTranslation()(RolesPermissionsModal);
